import React, { useCallback } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import { STEP_LINK_QUERY_PARAM_NAME } from 'Views/Onboarding/Pages/Survey/SurveyContext';
import {
  addQueryWithReplace, removeQuery,
} from 'Helpers/Query';
import { CLOSE_ON_RESOURCE_TYPE_QUERY_PARAM_NAME } from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Modals/OnboardingPlannerViewSurveyModal';

const OnboardingPlannerViewContext = React.createContext(undefined);

// Actions
export const ONBOARDING_PLANNER_VIEW_OPEN_SURVEY = 'onboarding-planner-view-open-survey';
export const ONBOARDING_PLANNER_VIEW_CLOSE_SURVEY = 'onboarding-planner-view-close-survey';
export const ONBOARDING_PLANNER_VIEW_SET_RESOURCE = 'onboarding-planner-view-set-resource';
export const ONBOARDING_PLANNER_VIEW_LOADING_STARTED = 'onboarding-planner-view-refresh-started';
export const ONBOARDING_PLANNER_VIEW_LOADING_SUCCEEDED = 'onboarding-planner-view-refresh-succeeded';
export const ONBOARDING_PLANNER_VIEW_LOADING_FAILED = 'onboarding-planner-view-refresh-failed';

// Initialize State
const init = () => {
  return {
    resource: null,
    loading: true,
    loadingError: null,
    surveyIsOpen: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case ONBOARDING_PLANNER_VIEW_OPEN_SURVEY:
      return {
        ...state,
        surveyIsOpen: true,
      };
    case ONBOARDING_PLANNER_VIEW_CLOSE_SURVEY: {
      if (!action.newResource) {
        return {
          ...state,
          surveyIsOpen: false,
        };
      }

      const updatedSection = state.resource.attr('onboardingSections').resources().list().find(section => section.attr('sectionData').resources().list()
        .find(v => {
          return v.id() === action.newResource?.id?.();
        }));
      let updatedViewResource = state.resource;

      if (updatedSection) {
        updatedSection.attr('sectionData').resources().updateItem(action.newResource);
        updatedViewResource = state.resource.copy();
      }

      return {
        ...state,
        surveyIsOpen: false,
        resource: updatedViewResource,
      };
    }
    case ONBOARDING_PLANNER_VIEW_SET_RESOURCE:
      return {
        ...state,
        resource: action.resource,
        loading: false,
        loadingError: null,
      };
    case ONBOARDING_PLANNER_VIEW_LOADING_STARTED:
      return {
        ...state,
        loading: true,
        loadingError: null,
      };
    case ONBOARDING_PLANNER_VIEW_LOADING_SUCCEEDED:
      return {
        ...state,
        loading: false,
        loadingError: null,
      };
    case ONBOARDING_PLANNER_VIEW_LOADING_FAILED:
      return {
        ...state,
        loading: false,
        loadingError: action.error,
      };
    default: {
      throw new Error(`Unhandled login-context action type: ${action.type}`);
    }
  }
};


const OnboardingPlannerViewProvider = ({
  householdResource,
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  // const stepLink = QueryVal(STEP_LINK_QUERY_PARAM_NAME);

  // useEffect(() => {
  //   if (state.surveyIsOpen) {
  //     dispatch({
  //       type: ONBOARDING_PLANNER_VIEW_OPEN_SURVEY,
  //     });
  //   }
  // }, [stepLink]);

  const loadOnboardingPlannerView = useCallback(async () => {
    try {
      dispatch({
        type: ONBOARDING_PLANNER_VIEW_LOADING_STARTED,
      });

      const [onboardingPlannerViewResource] = await householdResource.relationship(fr.relationships.onboardingPlannerView.name).link('related').get();

      dispatch({
        type: ONBOARDING_PLANNER_VIEW_SET_RESOURCE,
        resource: onboardingPlannerViewResource,
      });
    } catch (err) {
      dispatch({
        type: ONBOARDING_PLANNER_VIEW_LOADING_FAILED,
        error: err,
      });
    }
  }, [householdResource]);

  const openSurvey = useCallback(async (stepLink, closeOnStepType) => {
    addQueryWithReplace({ [STEP_LINK_QUERY_PARAM_NAME]: stepLink },
      { [CLOSE_ON_RESOURCE_TYPE_QUERY_PARAM_NAME]: closeOnStepType });
    dispatch({
      type: ONBOARDING_PLANNER_VIEW_OPEN_SURVEY,
    });
  }, []);

  const closeSurvey = useCallback(async (newResource) => {
    removeQuery(STEP_LINK_QUERY_PARAM_NAME, CLOSE_ON_RESOURCE_TYPE_QUERY_PARAM_NAME);
    dispatch({
      type: ONBOARDING_PLANNER_VIEW_CLOSE_SURVEY,
      newResource,
    });
  }, []);

  useComponentDidMount(() => {
    if (!householdResource.relationship(fr.relationships.onboardingPlannerView.name)) {
      return;
    }
    loadOnboardingPlannerView();
  });

  const memoizedValue = React.useMemo(() => {
    return {
      onboardingPlannerViewResource: state.resource,
      refreshOnboardingPlannerView: loadOnboardingPlannerView,
      loading: state.loading,
      loadingError: state.loadingError,
      surveyIsOpen: state.surveyIsOpen,
      openSurvey,
      closeSurvey,
    };
  }, [state, loadOnboardingPlannerView, openSurvey, closeSurvey]);

  return (
    <OnboardingPlannerViewContext.Provider value={memoizedValue}>
      {children}
    </OnboardingPlannerViewContext.Provider>
  );
};


const useOnboardingPlannerViewContext = () => {
  const context = React.useContext(OnboardingPlannerViewContext);

  if (context === undefined) {
    throw new Error('useOnboardingPlannerViewContext must be used within a OnboardingPlannerViewProvider');
  }

  return context;
};


OnboardingPlannerViewProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  householdResource: PropTypes.object.isRequired,
};


export {
  OnboardingPlannerViewProvider,
  useOnboardingPlannerViewContext,

};
